import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, CalendarCheck, BookOpen, ChevronDown, Search, BedDouble, Sparkles } from 'lucide-react';
import { IMAGES, imgAt, heroSrcSet } from '../mock';

const SLIDES = [
  { img: IMAGES.beachBurjAlArab, caption: 'Jumeirah Beach & the Burj Al Arab' },
  { img: IMAGES.burjFountain, caption: 'The Dubai Fountain & Burj Khalifa' },
  { img: IMAGES.desertDunes, caption: 'Golden dunes on a desert safari' },
  { img: IMAGES.mosqueWide, caption: 'Sheikh Zayed Grand Mosque, Abu Dhabi' },
  { img: IMAGES.jbrBeach, caption: 'JBR Beach & the Marina skyline' },
  { img: IMAGES.palmJumeirah, caption: 'Palm Jumeirah from the air' },
  { img: IMAGES.marinaDay, caption: 'Dubai Marina from the water' },
  { img: IMAGES.marinaDusk, caption: 'Dubai Marina at dusk' },
];

const EMIRATE_OPTS = [
  'All emirates', 'Dubai', 'Abu Dhabi', 'Sharjah', 'Ras Al Khaimah', 'Fujairah', 'Ajman',
];
const BUDGET_OPTS = [
  { value: 'all', label: 'Any budget' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'mid', label: 'Mid-range' },
  { value: 'budget', label: 'Budget' },
];
const TRIP_OPTS = ['Any trip', 'Honeymoon', 'Family', 'Beach', 'Business', 'Budget', 'City break'];

const PILLS = [
  { icon: MapPin, label: '7 emirates', sub: 'one country', pos: 'top-[18%] right-[6%]', delay: '0s' },
  { icon: CalendarCheck, label: '30 days', sub: 'visa-free for Aussies', pos: 'top-[44%] right-[10%]', delay: '1.2s' },
  { icon: BookOpen, label: '120+ guides', sub: 'updated for 2026', pos: 'bottom-[16%] right-[7%]', delay: '2.1s' },
];

const HEADLINE = ['Everything', 'you', 'need', 'to', 'know', 'before', 'you', 'land', 'in', 'the', 'Emirates'];

const Hero = () => {
  const [active, setActive] = useState(0);
  const [emirate, setEmirate] = useState('All emirates');
  const [budget, setBudget] = useState('all');
  const [trip, setTrip] = useState('Any trip');
  const [offset, setOffset] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const navigate = useNavigate();

  // Honour the visitor's reduced-motion preference.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
    };
  }, []);

  // Auto-rotate slides — paused when reduced motion is preferred.
  useEffect(() => {
    if (reduceMotion) return;
    const t = setInterval(() => setActive((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [reduceMotion]);

  // Gentle parallax — disabled when reduced motion is preferred.
  useEffect(() => {
    if (reduceMotion) {
      setOffset(0);
      return;
    }
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        setOffset(window.scrollY);
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [reduceMotion]);

  const findHotels = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (emirate && emirate !== 'All emirates') params.set('emirate', emirate);
    if (budget && budget !== 'all') params.set('budget', budget);
    if (trip && trip !== 'Any trip') params.set('tripType', trip);
    const qs = params.toString();
    navigate(qs ? `/hotels?${qs}` : '/hotels');
  };

  return (
    <section className="relative overflow-hidden">
      <div className="relative min-h-[78vh] flex items-center">
        {/* Rotating slides */}
        {SLIDES.map((s, i) => (
          <div
            key={i}
            className={`hero-slide absolute inset-0 ${i === active ? 'opacity-100' : 'opacity-0'}`}
            style={{ transform: `translate3d(0, ${Math.min(offset, 900) * 0.06}px, 0) scale(1)` }}
            aria-hidden={i !== active}
          >
            <img
              src={imgAt(s.img, 1920)}
              srcSet={heroSrcSet(s.img)}
              sizes="100vw"
              alt={s.caption}
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchpriority={i === 0 ? 'high' : 'low'}
              decoding="async"
              className={`w-full h-full object-cover object-center ${i === active && !reduceMotion ? 'kenburns' : ''}`}
            />
          </div>
        ))}

        {/* Scrims */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1410]/85 via-[#1A1410]/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410]/75 via-transparent to-[#1A1410]/10" />

        {/* Warm sunset glow */}
        <div className="pointer-events-none absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full hero-glow"
          style={{ background: 'radial-gradient(circle, rgba(246,165,74,0.55) 0%, rgba(182,138,53,0.25) 45%, transparent 70%)' }} />

        {/* Floating glass pills (desktop) */}
        <div className="hidden lg:block">
          {PILLS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                className={`absolute ${p.pos} floaty`}
                style={{ animationDelay: p.delay }}
              >
                <div className="flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-5 py-3.5 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)]">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl sunset-gradient text-white shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="leading-tight">
                    <span className="block font-display text-[20px] font-semibold text-white">{p.label}</span>
                    <span className="block text-[12px] text-white/70">{p.sub}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-5 w-full">
          <div className="max-w-2xl py-24">
            <span className="hero-rise inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 text-[12px] font-semibold tracking-[0.16em] uppercase text-[#f6d9ad]"
              style={{ animationDelay: '0.05s' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#CBA344] animate-pulse" />
              Independent UAE Travel &amp; Relocation Guide
            </span>

            <h1 className="mt-6 font-display text-[46px] sm:text-[64px] leading-[1.02] font-semibold text-white">
              {HEADLINE.map((w, i) => (
                <span
                  key={i}
                  className="hero-rise inline-block mr-[0.26em]"
                  style={{ animationDelay: `${0.18 + i * 0.055}s` }}
                >
                  {w === 'Emirates' ? <span className="sunset-text">{w}</span> : w}
                </span>
              ))}
            </h1>

            <p className="hero-rise mt-7 text-[19px] leading-relaxed text-[#dbe5ee] max-w-xl"
              style={{ animationDelay: '1s' }}>
              Visas, neighbourhoods, hotels, culture and real cost-of-living numbers —
              written for visitors and future expats, kept current, and always honest
              about what&apos;s sponsored.
            </p>

            <div className="hero-rise mt-9 flex flex-wrap gap-4" style={{ animationDelay: '1.18s' }}>
              <Link to="/visas-entry" className="btn-sunset inline-flex items-center rounded-full px-7 py-3.5 text-[16px] font-semibold">
                Start with Visa Rules →
              </Link>
              <Link to="/emirates" className="inline-flex items-center rounded-full px-7 py-3.5 text-[16px] font-semibold text-white border border-white/50 hover:bg-white/10 transition-colors backdrop-blur-sm">
                Explore the Emirates
              </Link>
            </div>

            {/* In-hero hotel search */}
            <form
              onSubmit={findHotels}
              className="hero-rise mt-7 w-full max-w-2xl rounded-2xl bg-white/12 backdrop-blur-md border border-white/20 p-2.5 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.65)]"
              style={{ animationDelay: '1.34s' }}
            >
              <div className="flex flex-col sm:flex-row gap-2.5">
                <label className="flex-1 flex items-center gap-2 rounded-xl bg-white/90 px-3.5 py-2.5">
                  <MapPin className="w-4 h-4 text-[#e0564e] shrink-0" />
                  <select
                    value={emirate}
                    onChange={(ev) => setEmirate(ev.target.value)}
                    aria-label="Choose an emirate"
                    className="w-full bg-transparent text-[15px] font-medium text-[#1B1D21] outline-none cursor-pointer"
                  >
                    {EMIRATE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="flex-1 flex items-center gap-2 rounded-xl bg-white/90 px-3.5 py-2.5">
                  <Sparkles className="w-4 h-4 text-[#92722A] shrink-0" />
                  <select
                    value={trip}
                    onChange={(ev) => setTrip(ev.target.value)}
                    aria-label="Choose a trip type"
                    className="w-full bg-transparent text-[15px] font-medium text-[#1B1D21] outline-none cursor-pointer"
                  >
                    {TRIP_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="flex-1 flex items-center gap-2 rounded-xl bg-white/90 px-3.5 py-2.5">
                  <BedDouble className="w-4 h-4 text-[#2F663C] shrink-0" />
                  <select
                    value={budget}
                    onChange={(ev) => setBudget(ev.target.value)}
                    aria-label="Choose a budget"
                    className="w-full bg-transparent text-[15px] font-medium text-[#1B1D21] outline-none cursor-pointer"
                  >
                    {BUDGET_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <button type="submit" className="btn-sunset inline-flex items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-[15px] font-semibold whitespace-nowrap">
                  <Search className="w-4 h-4" /> Find hotels
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Caption + slide dots */}
        <div className="absolute bottom-6 left-0 right-0">
          <div className="max-w-7xl mx-auto px-5 flex items-end justify-between gap-4">
            <div className="flex items-center gap-2.5">
              {SLIDES.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Show ${s.caption}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === active ? 'w-9 sunset-gradient' : 'w-4 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
            <span className="hidden sm:block text-[13px] text-white/70 font-medium tracking-wide">
              {SLIDES[active].caption}
            </span>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center">
          <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center p-1.5">
            <span className="scroll-dot w-1 h-1.5 rounded-full bg-white/80" />
          </div>
          <ChevronDown className="w-4 h-4 text-white/40 -mt-1" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
